/**
 * The integration's mark in a tile, or a plug when it has none. The tile is
 * what makes the list read as a catalogue rather than a settings form.
 *
 * The catalogue row's `iconUrl` is the first choice, but not every connector
 * has one — custom integrations like PlainSail carry none — so the shared
 * fallback map fills the gap before the generic plug does. It is the same
 * resolver the personal Plugins page uses, so a connector cannot show a real
 * mark on one catalogue and a plug on the other.
 *
 * @module components/apps/integrations/IntegrationIcon
 */
import { PlugZapIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { isFullTileIcon, resolveConnectorIconFallback } from "~/lib/connectorIcons";
import { cn } from "~/lib/utils";
import type { Integration } from "../../../state/governance";

export function IntegrationIcon({
  integration,
  className,
  imageClassName,
}: {
  readonly integration: Pick<Integration, "iconUrl" | "name" | "id" | "toolkitSlug">;
  readonly className?: string | undefined;
  readonly imageClassName?: string | undefined;
}) {
  const fallbackIconUrl = useMemo(
    () =>
      resolveConnectorIconFallback({
        id: integration.id,
        toolkitSlug: integration.toolkitSlug,
      }),
    [integration.id, integration.toolkitSlug],
  );
  // A mark vendored with the app (/integrations/…) is the one we chose for a
  // partner whose catalogue icon is missing or unreadable; it wins.
  const preferred = fallbackIconUrl?.startsWith("/integrations/")
    ? fallbackIconUrl
    : (integration.iconUrl ?? fallbackIconUrl);
  const [iconSrc, setIconSrc] = useState<string | null>(preferred);

  useEffect(() => {
    setIconSrc(preferred);
  }, [preferred]);

  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-border/60 bg-background p-1.5",
        className,
        // A mark that is its own tile fills the box: no frame, no padding.
        isFullTileIcon(iconSrc) && "border-0 bg-transparent p-0",
      )}
    >
      {iconSrc ? (
        <img
          src={iconSrc}
          alt=""
          className={cn("size-full object-contain", imageClassName)}
          onError={() => {
            // One step down the chain per failure, so a dead catalogue URL
            // still lands on the known mark before giving up on the plug.
            setIconSrc(
              fallbackIconUrl && iconSrc !== fallbackIconUrl ? fallbackIconUrl : null,
            );
          }}
        />
      ) : (
        <PlugZapIcon className="size-1/2 text-muted-foreground" />
      )}
    </span>
  );
}
